import {
  motion,
  useScroll,
  useMotionValueEvent,
  AnimatePresence,
} from 'framer-motion'
import {
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'
import { Link, useLocation } from 'react-router-dom'
import YaslanLogo from './YaslanLogo'
import LanguageSwitcher from './LanguageSwitcher'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'
import { getSectorById, type SectorId } from '../data/sectors'

// ───────────── Menu data ─────────────

type MenuItem = {
  label: string
  desc: string
  href: string
  icon: ReactNode
}

type MenuColumn = {
  title: string
  items: MenuItem[]
}

type Menu = {
  key: string
  label: string
  columns: MenuColumn[]
}

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

// Icônes (inchangées) — extraites au niveau module pour rester verbatim.
const ICONS = {
  storage: (
    <svg {...ICON_PROPS}>
      <rect x="3" y="13" width="18" height="8" rx="1" />
      <path d="M3 17h18" />
      <path d="M9 13V9h6v4" />
    </svg>
  ),
  pickpack: (
    <svg {...ICON_PROPS}>
      <rect x="3" y="8" width="18" height="13" rx="1" />
      <path d="M12 8v13" />
      <path d="M3 8V5h18v3" />
      <path d="M8 5c0-2 4-2 4 0c0-2 4-2 4 0" />
    </svg>
  ),
  shipping: (
    <svg {...ICON_PROPS}>
      <rect x="2" y="9" width="11" height="8" rx="1" />
      <path d="M13 11h4l3 3v3h-7" />
      <circle cx="6.5" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </svg>
  ),
  returns: (
    <svg {...ICON_PROPS}>
      <path d="M3 7h11a5 5 0 0 1 0 10H8" />
      <path d="M6 4L3 7l3 3" />
    </svg>
  ),
  fulfillment: (
    <svg {...ICON_PROPS}>
      <path d="M3 8l9-5 9 5-9 5-9-5z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <path d="M12 13v8" />
      <path d="M16.5 5.5l1 1M18.5 3.5l.6.6" />
    </svg>
  ),
  integrations: (
    <svg {...ICON_PROPS}>
      <path d="M9 4v4M15 4v4" />
      <rect x="7" y="8" width="10" height="6" rx="1" />
      <path d="M12 14v4M9 19h6" />
    </svg>
  ),
  carriers: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  ),
  mode: (
    <svg {...ICON_PROPS}>
      <path d="M12 4l-2 2c0 1 1 2 2 2s2-1 2-2L12 4z" />
      <path d="M12 8L4 14l3 1 5-3 5 3 3-1-8-6z" />
      <path d="M7 15v4h10v-4" />
    </svg>
  ),
  cosmetique: (
    <svg {...ICON_PROPS}>
      <rect x="9" y="3" width="6" height="3" rx="1" />
      <path d="M8 6h8l-1 4H9z" />
      <rect x="8" y="10" width="8" height="11" rx="2" />
    </svg>
  ),
  parfumerie: (
    <svg {...ICON_PROPS}>
      <rect x="9" y="3" width="6" height="3" />
      <path d="M8 6h8v2H8z" />
      <rect x="7" y="8" width="10" height="13" rx="2" />
      <path d="M17 11l3-1M17 13l3 1" />
    </svg>
  ),
  maison: (
    <svg {...ICON_PROPS}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-5h4v5" />
    </svg>
  ),
  jardin: (
    <svg {...ICON_PROPS}>
      <path d="M12 21v-7" />
      <path d="M12 14c-3 0-6-3-6-7 4 0 6 3 6 7z" />
      <path d="M12 14c3 0 6-3 6-7-4 0-6 3-6 7z" />
    </svg>
  ),
  electronique: (
    <svg {...ICON_PROPS}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 6V3M15 6V3M9 21v-3M15 21v-3M3 9h3M3 15h3M18 9h3M18 15h3" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  ),
  adr: (
    <svg {...ICON_PROPS}>
      <path d="M12 3l9 9-9 9-9-9z" />
      <path d="M12 8.5v4.5M12 16h.01" />
    </svg>
  ),
  afsca: (
    <svg {...ICON_PROPS}>
      <path d="M5 20c8 1 14-4 14-13-9 0-15 4-14 13z" />
      <path d="M5 20c2-5 5-8 9-10" />
    </svg>
  ),
  blog: (
    <svg {...ICON_PROPS}>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </svg>
  ),
  faq: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.6-2.5 1.4-2.5 3.4" />
      <path d="M12 17h.01" />
    </svg>
  ),
  about: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
    </svg>
  ),
  contact: (
    <svg {...ICON_PROPS}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  ),
}

// ───────────── Component ─────────────

export default function Nav() {
  const { scrollY } = useScroll()
  const { t } = useT()
  const locale = useLocale()
  const [scrolled, setScrolled] = useState(false)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [panelLeft, setPanelLeft] = useState<number | null>(null)
  const location = useLocation()
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const navRef = useRef<HTMLDivElement | null>(null)

  // Résout le slug d'un secteur pour la langue courante (FR inchangé, EN/NL localisés).
  const sectorSlug = (id: SectorId) => getSectorById(id)!.slug[locale]

  // Données de menu construites avec t() + localizedPath (liens internes localisés).
  const MENUS: Menu[] = [
    {
      key: 'services',
      label: t('nav.menuServices'),
      columns: [
        {
          title: t('nav.colParService'),
          items: [
            {
              label: t('nav.storageLabel'),
              desc: t('nav.storageDesc'),
              href: localizedPath('service.storage', locale),
              icon: ICONS.storage,
            },
            {
              label: t('nav.pickpackLabel'),
              desc: t('nav.pickpackDesc'),
              href: localizedPath('service.pickpack', locale),
              icon: ICONS.pickpack,
            },
            {
              label: t('nav.shippingLabel'),
              desc: t('nav.shippingDesc'),
              href: localizedPath('service.shipping', locale),
              icon: ICONS.shipping,
            },
            {
              label: t('nav.returnsLabel'),
              desc: t('nav.returnsDesc'),
              href: localizedPath('service.returns', locale),
              icon: ICONS.returns,
            },
            {
              label: t('nav.fulfillmentLabel'),
              desc: t('nav.fulfillmentDesc'),
              href: localizedPath('fulfillment', locale),
              icon: ICONS.fulfillment,
            },
          ],
        },
        {
          title: t('nav.colOutils'),
          items: [
            {
              label: t('nav.integrationsLabel'),
              desc: t('nav.integrationsDesc'),
              href: '/#integrations',
              icon: ICONS.integrations,
            },
            {
              label: t('nav.carriersLabel'),
              desc: t('nav.carriersDesc'),
              href: '/#carriers',
              icon: ICONS.carriers,
            },
          ],
        },
      ],
    },
    {
      key: 'secteurs',
      label: t('nav.menuSecteurs'),
      columns: [
        {
          title: t('nav.colModeBeaute'),
          items: [
            {
              label: t('nav.modeLabel'),
              desc: t('nav.modeDesc'),
              href: localizedPath('sectors.detail', locale, { slug: sectorSlug('mode') }),
              icon: ICONS.mode,
            },
            {
              label: t('nav.cosmetiqueLabel'),
              desc: t('nav.cosmetiqueDesc'),
              href: localizedPath('sectors.detail', locale, { slug: sectorSlug('cosmetique') }),
              icon: ICONS.cosmetique,
            },
            {
              label: t('nav.parfumerieLabel'),
              desc: t('nav.parfumerieDesc'),
              href: localizedPath('sectors.detail', locale, { slug: sectorSlug('parfumerie') }),
              icon: ICONS.parfumerie,
            },
          ],
        },
        {
          title: t('nav.colMaisonOutdoor'),
          items: [
            {
              label: t('nav.maisonLabel'),
              desc: t('nav.maisonDesc'),
              href: localizedPath('sectors.detail', locale, { slug: sectorSlug('maison-decoration') }),
              icon: ICONS.maison,
            },
            {
              label: t('nav.jardinLabel'),
              desc: t('nav.jardinDesc'),
              href: localizedPath('sectors.detail', locale, { slug: sectorSlug('jardin-exterieur') }),
              icon: ICONS.jardin,
            },
            {
              label: t('nav.electroniqueLabel'),
              desc: t('nav.electroniqueDesc'),
              href: localizedPath('sectors.detail', locale, { slug: sectorSlug('electronique') }),
              icon: ICONS.electronique,
            },
          ],
        },
      ],
    },
    {
      key: 'expertises',
      label: t('nav.menuExpertises'),
      columns: [
        {
          title: t('nav.colExpertise'),
          items: [
            {
              label: t('nav.adrLabel'),
              desc: t('nav.adrDesc'),
              href: localizedPath('expertise.adr', locale),
              icon: ICONS.adr,
            },
            {
              label: t('nav.afscaLabel'),
              desc: t('nav.afscaDesc'),
              href: localizedPath('expertise.afsca', locale),
              icon: ICONS.afsca,
            },
          ],
        },
      ],
    },
    {
      key: 'ressources',
      label: t('nav.menuRessources'),
      columns: [
        {
          title: t('nav.colLecture'),
          items: [
            {
              label: t('nav.blogLabel'),
              desc: t('nav.blogDesc'),
              href: localizedPath('blog', locale),
              icon: ICONS.blog,
            },
            {
              label: t('nav.faqLabel'),
              desc: t('nav.faqDesc'),
              href: '/#faq',
              icon: ICONS.faq,
            },
          ],
        },
        {
          title: t('nav.colSociete'),
          items: [
            {
              label: t('nav.aboutLabel'),
              desc: t('nav.aboutDesc'),
              href: localizedPath('about', locale),
              icon: ICONS.about,
            },
            {
              label: t('nav.contactLabel'),
              desc: t('nav.contactDesc'),
              href: localizedPath('contact', locale),
              icon: ICONS.contact,
            },
          ],
        },
      ],
    },
  ]

  // Position the dropdown panel centered under the active trigger
  useEffect(() => {
    if (!openMenu) return
    const trigger = triggerRefs.current[openMenu]
    const wrap = navRef.current
    if (!trigger || !wrap) return
    const tRect = trigger.getBoundingClientRect()
    const wRect = wrap.getBoundingClientRect()
    setPanelLeft(tRect.left + tRect.width / 2 - wRect.left)
    const onResize = () => {
      const t2 = triggerRefs.current[openMenu]
      const w2 = navRef.current
      if (!t2 || !w2) return
      const tr = t2.getBoundingClientRect()
      const wr = w2.getBoundingClientRect()
      setPanelLeft(tr.left + tr.width / 2 - wr.left)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [openMenu, scrolled])

  useMotionValueEvent(scrollY, 'change', (v) => setScrolled(v > 24))

  useEffect(() => {
    setMobileOpen(false)
    setOpenMenu(null)
  }, [location.pathname, location.hash])

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMenu(null)
        setMobileOpen(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const open = (key: string) => {
    cancelClose()
    setOpenMenu(key)
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenMenu(null), 160)
  }

  const activeMenu = MENUS.find((m) => m.key === openMenu) ?? null

  // Active matching for dropdown trigger
  const isMenuActive = (menu: Menu) => {
    return menu.columns.some((c) =>
      c.items.some((it) => {
        if (it.href.startsWith('/') && !it.href.includes('#')) {
          return location.pathname === it.href || location.pathname.startsWith(it.href + '/')
        }
        return false
      }),
    )
  }

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4"
    >
      <div className="max-w-6xl w-full relative" ref={navRef}>
        {/*
          `lg:min-w-[900px]` = plancher au rétrécissement de 82 %. Sur les écrans
          desktop étroits (1024–1150 px), 82 % de la largeur disponible tombe SOUS
          la largeur du contenu (~882 px en NL, la langue la plus verbeuse) et le
          contenu sortirait de la pilule. Le plancher borne l'animation sans la
          casser. Voir aussi `whitespace-nowrap` sur le CTA plus bas.
        */}
        <motion.nav
          animate={{
            width: scrolled ? '82%' : '100%',
            // 0.88 au repos : la pilule reste blanche et nette même posée sur
            // le hero sombre (à 0.42 elle devenait un verre gris illisible).
            backgroundColor: scrolled
              ? 'rgba(255,255,255,0.78)'
              : 'rgba(255,255,255,0.88)',
            borderColor: scrolled ? 'rgba(14,14,20,0.08)' : 'rgba(14,14,20,0.06)',
          }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto flex items-center justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] lg:min-w-[900px] gap-4 rounded-full border backdrop-blur-xl pl-5 pr-2 py-2"
          style={{
            boxShadow: scrolled
              ? '0 10px 40px -20px rgba(14,14,20,0.15)'
              : 'none',
          }}
        >
          {/* Logo (col 1, left-aligned) */}
          <Link
            to="/"
            className="flex items-center gap-2 justify-self-start"
            onClick={() => {
              setMobileOpen(false)
              setOpenMenu(null)
            }}
          >
            <YaslanLogo variant="black" height={22} />
          </Link>

          {/* Desktop dropdown triggers (col 2, centered) */}
          <ul className="hidden lg:flex items-center gap-0.5 text-sm justify-self-center">
            {MENUS.map((menu) => {
              const isOpen = openMenu === menu.key
              const isActive = isMenuActive(menu)
              return (
                <li
                  key={menu.key}
                  onMouseEnter={() => open(menu.key)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    ref={(el) => {
                      triggerRefs.current[menu.key] = el
                    }}
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpenMenu((prev) => (prev === menu.key ? null : menu.key))
                    }
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-full transition-colors ${
                      isOpen
                        ? 'text-ink-950 bg-white/70'
                        : isActive
                          ? 'text-accent-700 hover:bg-white/60'
                          : 'text-ink-600 hover:text-ink-950 hover:bg-white/60'
                    }`}
                  >
                    {menu.label}
                    <motion.svg
                      width="10"
                      height="10"
                      viewBox="0 0 12 12"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="-mr-0.5"
                    >
                      <path
                        d="M3 5l3 3 3-3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </motion.svg>
                  </button>
                </li>
              )
            })}
            {/* Lien direct Tarifs — page money, pas de dropdown */}
            <li>
              <Link
                to={localizedPath('pricing', locale)}
                className={`inline-flex items-center px-3 py-2 rounded-full transition-colors ${
                  location.pathname === localizedPath('pricing', locale)
                    ? 'text-accent-700 hover:bg-white/60'
                    : 'text-ink-600 hover:text-ink-950 hover:bg-white/60'
                }`}
              >
                {t('nav.pricingLink')}
              </Link>
            </li>
          </ul>

          {/* Right side: CTA + hamburger (col 3, right-aligned) */}
          <div className="flex items-center gap-1.5 justify-self-end">
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>
            {/*
              `whitespace-nowrap` est structurel, pas cosmétique : la grille
              `1fr_auto_1fr` répartit l'espace libre à parts égales entre les deux
              colonnes latérales, donc la colonne de droite reçoit ~208 px alors
              qu'elle en demande ~284. Sans nowrap, le libellé se replie sur 3–4
              lignes et la pilule enfle de 54 à 114 px de haut au scroll.
            */}
            <Link
              to={localizedPath('contact', locale)}
              className="hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium bg-ink-950 text-white rounded-full px-4 py-2 hover:bg-ink-800 transition-colors"
            >
              {t('nav.cta')}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>

            <button
              type="button"
              aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={mobileOpen}
              onClick={() => {
                setMobileOpen((o) => !o)
                setOpenMenu(null)
              }}
              className="lg:hidden relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/60 transition-colors"
            >
              <span className="relative w-5 h-4 flex flex-col justify-between">
                <motion.span
                  animate={{
                    rotate: mobileOpen ? 45 : 0,
                    y: mobileOpen ? 7 : 0,
                  }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="block h-0.5 w-full bg-ink-950 rounded-full origin-center"
                />
                <motion.span
                  animate={{
                    opacity: mobileOpen ? 0 : 1,
                    scaleX: mobileOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                  className="block h-0.5 w-full bg-ink-950 rounded-full"
                />
                <motion.span
                  animate={{
                    rotate: mobileOpen ? -45 : 0,
                    y: mobileOpen ? -7 : 0,
                  }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="block h-0.5 w-full bg-ink-950 rounded-full origin-center"
                />
              </span>
            </button>
          </div>
        </motion.nav>

        {/* ───────────── Desktop dropdown panel ───────────── */}
        <AnimatePresence>
          {activeMenu && (
            <div
              key={activeMenu.key + '-pos'}
              style={{
                left: panelLeft != null ? `${panelLeft}px` : '50%',
                transform: 'translateX(-50%)',
              }}
              className="hidden lg:block absolute top-full mt-3"
            >
            <motion.div
              key={activeMenu.key}
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              className="origin-top"
            >
              <div className="relative rounded-3xl border border-ink-100 bg-white/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(14,14,20,0.18)] overflow-hidden">
                {/* halo */}
                <motion.div
                  aria-hidden
                  animate={{ opacity: [0.35, 0.6, 0.35] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  className="pointer-events-none absolute -top-24 -right-20 w-64 h-64 rounded-full blur-3xl"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)',
                  }}
                />
                <div
                  className={`relative grid gap-6 p-5 ${
                    activeMenu.columns.length === 2
                      ? 'grid-cols-2 min-w-[640px]'
                      : 'grid-cols-1 min-w-[360px]'
                  }`}
                >
                  {activeMenu.columns.map((col) => (
                    <div key={col.title} className="min-w-[300px]">
                      <p className="px-3 text-[10px] font-semibold tracking-[0.22em] uppercase text-ink-500">
                        {col.title}
                      </p>
                      <ul className="mt-2 space-y-0.5">
                        {col.items.map((item) => (
                          <li key={item.href}>
                            <DropdownItem item={item} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* ───────────── Mobile menu ───────────── */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setMobileOpen(false)}
                className="lg:hidden fixed inset-0 bg-ink-950/30 backdrop-blur-sm -z-10"
              />
              <motion.div
                key="panel"
                initial={{ opacity: 0, y: -16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="lg:hidden absolute top-full left-0 right-0 mt-3 rounded-3xl border border-ink-100 bg-white/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(14,14,20,0.25)] overflow-hidden max-h-[85vh] overflow-y-auto"
              >
                <div className="p-3 space-y-4">
                  {/* Lien direct Tarifs (mobile) */}
                  <Link
                    to={localizedPath('pricing', locale)}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between rounded-2xl border border-accent-100 bg-accent-50/70 p-3.5 hover:bg-accent-50 transition-colors"
                  >
                    <span className="text-sm font-semibold text-ink-950">
                      {t('nav.pricingLink')}
                    </span>
                    <span aria-hidden className="text-accent-600">→</span>
                  </Link>
                  {MENUS.map((menu, mi) => (
                    <motion.div
                      key={menu.key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: 0.04 + mi * 0.05,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      <p className="px-3 text-[10px] font-semibold tracking-[0.22em] uppercase text-ink-500">
                        {menu.label}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {menu.columns.flatMap((c) => c.items).map((item) => (
                          <li key={item.href}>
                            <DropdownItem
                              item={item}
                              compact
                              onNavigate={() => setMobileOpen(false)}
                            />
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
                <div className="p-3 border-t border-ink-100 bg-ink-50/40">
                  <div className="mb-3 flex justify-center">
                    <LanguageSwitcher compact />
                  </div>
                  <Link
                    to={localizedPath('contact', locale)}
                    onClick={() => setMobileOpen(false)}
                    className="relative group flex items-center justify-center gap-2 w-full bg-ink-950 hover:bg-ink-800 text-white rounded-2xl px-5 py-4 text-sm font-semibold transition-colors overflow-hidden"
                  >
                    <span
                      aria-hidden
                      className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-ink-100/20 to-transparent pointer-events-none"
                    />
                    <span className="relative">{t('nav.cta')}</span>
                    <svg
                      className="relative"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                    >
                      <path
                        d="M5 12h14M13 5l7 7-7 7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  )
}

// ───────────── Dropdown item ─────────────

function DropdownItem({
  item,
  compact = false,
  onNavigate,
}: {
  item: MenuItem
  compact?: boolean
  onNavigate?: () => void
}) {
  const isInternal = item.href.startsWith('/') && !item.href.includes('#')
  const className = `group flex items-start gap-3 rounded-2xl ${
    compact ? 'p-2.5' : 'p-3'
  } hover:bg-accent-50 transition-colors`

  const content = (
    <>
      <div className="w-9 h-9 rounded-xl bg-accent-50 group-hover:bg-ink-100 border border-accent-100 group-hover:border-accent-200 flex items-center justify-center text-accent-700 group-hover:text-accent-800 shrink-0 transition-colors">
        {item.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink-950 group-hover:text-accent-800 transition-colors">
          {item.label}
        </p>
        <p
          className={`mt-0.5 text-xs text-ink-500 leading-snug ${
            compact ? 'line-clamp-1' : ''
          }`}
        >
          {item.desc}
        </p>
      </div>
      <span
        aria-hidden
        className="self-center text-ink-300 group-hover:text-accent-600 group-hover:translate-x-0.5 transition-all"
      >
        →
      </span>
    </>
  )

  if (isInternal) {
    return (
      <Link to={item.href} onClick={onNavigate} className={className}>
        {content}
      </Link>
    )
  }
  return (
    <a href={item.href} onClick={onNavigate} className={className}>
      {content}
    </a>
  )
}
